import { Check, Cancel } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Select,
    DialogProps,
    SelectChangeEvent,
    MenuItem,
    TextField,
    Stack,
    Box
} from '@mui/material';
import { AxiosError } from 'axios';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useGet } from '../../../lib/api/hooks';
import { ErrorResponse } from '../../../lib/dtos/ErrorResponse';
import { ReviewExtendReasonDto, ReviewTimeDto } from '../../../lib/dtos/ReviewDtos';
import { useScreenSmallerThan } from '../../../lib/hooks/breakpoint-hooks';
import { ClinicalReviewSystemTime } from '../../../lib/models/SystemLoggedTime';

export type SubmitReviewModalProps = Pick<DialogProps, 'open'> & {
    reviewedMinutes: number;
    reviewExtendReason: string;
    suggestedSystemTime?: ClinicalReviewSystemTime;
    isAddendum?: boolean;
    onClose: (
        event: unknown,
        reason: 'backdropClick' | 'escapeKeyDown' | 'clickCancelButton' | 'clickSubmitButton'
    ) => void;
    onSubmit: (data: SubmitReviewModalResult) => void | Promise<void>;
    handleSubmitTimeChange: (reviewedMinutes: number, reviewExtendReason: string) => void;
};

export type SubmitReviewModalResult = {
    reviewedMinutes: number;
    reviewExtendReason: string;
};

const SubmitReviewModal = ({
    open,
    reviewedMinutes,
    reviewExtendReason,
    suggestedSystemTime,
    isAddendum,
    onClose,
    onSubmit,
    handleSubmitTimeChange
}: SubmitReviewModalProps) => {
    const [resolved, setResolved] = React.useState(true);
    const [reviewTime, setReviewTime] = React.useState(reviewedMinutes);
    const [isReasonShow, setIsReasonShow] = React.useState(reviewTime > 20);
    const [defaultReviewTime, setDefaultReviewTime] = React.useState(0);
    const [reasonForExtendedReview, setReasonForExtendedReview] = React.useState(
        reviewTime > 20 ? reviewExtendReason : ''
    );
    const [otherFreeText, setOtherFreeText] = React.useState('');

    const { loading: reviewTimesLoading, data: reviewTimes } =
        useGet<ReviewTimeDto[]>('casedetails/reviewtimes');
    const { loading: reviewExtendReasonsLoading, data: reviewExtendReasons } = useGet<
        ReviewExtendReasonDto[]
    >('casedetails/reviewextendreasons');
    const isSmallerThanSmall = useScreenSmallerThan('sm');

    useEffect(() => {
        const defVal = reviewTimes?.find((rt) => rt.isDefault)?.minutes || 0;
        setReviewTime(reviewedMinutes !== 0 ? reviewedMinutes : defVal);
        setDefaultReviewTime(defVal);
        setIsReasonShow(reviewTime > 20);
        setReasonForExtendedReview(reviewTime > 20 ? reviewExtendReason : '');
    }, [open, reviewTimes, reviewExtendReasons, reviewExtendReason, reviewedMinutes, reviewTime]);

    useEffect(() => {
        setReasonForExtendedReview(reviewTime > 20 ? reviewExtendReason : '');
    }, [open, reviewTime, reviewExtendReason]);

    useEffect(() => {
        handleSubmitTimeChange(reviewTime, reasonForExtendedReview);
    }, [reviewTime, reasonForExtendedReview, handleSubmitTimeChange]);

    const onSubmitHandler: React.FormEventHandler = async (e) => {
        e.preventDefault();

        setResolved(false);

        try {
            const data: SubmitReviewModalResult = {
                reviewedMinutes: reviewTime,
                reviewExtendReason:
                    reasonForExtendedReview === 'other' ? otherFreeText : reasonForExtendedReview
            };
            await onSubmit.call(null, data);
            onClose({}, 'clickSubmitButton');
        } catch (ex) {
            const error = ex as AxiosError<ErrorResponse>;
            onClose({}, 'clickSubmitButton');
            toast.error('Failed updating the field: ' + error.response?.data.message, {
                autoClose: 5000
            });
        } finally {
            setResolved(true);
        }
    };

    const clickCancelHandler = () => {
        onClose({}, 'clickCancelButton');
        setTimeout(() => {
            setReviewTime(0);
            setReasonForExtendedReview('');
            setOtherFreeText('');
        }, 200);
    };

    const reviewTimeChangeHandler = (e: SelectChangeEvent, child: React.ReactNode) => {
        const time = parseInt(e.target.value);
        setReviewTime(time);
        setIsReasonShow(time !== defaultReviewTime && time > 20);
    };

    const reasonForExtendedReviewChangeHandler = (e: SelectChangeEvent, child: React.ReactNode) => {
        setReasonForExtendedReview(e.target.value);
    };

    const otherFreeTextChangeHandler: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
        setOtherFreeText(e.target.value);
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <form onSubmit={onSubmitHandler}>
                <DialogTitle sx={{ fontSize: 20, fontWeight: (t) => t.typography.fontWeightBold }}>
                    {isAddendum ? 'Submit Medical Addendum' : 'Submit Review'}
                </DialogTitle>
                <DialogContent>
                    <Stack gap={2}>
                        <Box>
                            {suggestedSystemTime !== undefined &&
                                suggestedSystemTime?.suggestedSystemTime !== '' && (
                                    <DialogContentText sx={{ paddingBottom: '3%' }}>
                                        Suggested {isAddendum ? 'medical addendum' : 'review'} time:{' '}
                                        {suggestedSystemTime?.suggestedSystemTime}
                                    </DialogContentText>
                                )}
                            <DialogContentText>
                                How long did this {isAddendum ? 'medical addendum' : 'review'} take?
                            </DialogContentText>
                            <Select
                                fullWidth
                                size="small"
                                value={reviewTime.toString()}
                                onChange={reviewTimeChangeHandler}
                                MenuProps={{
                                    PaperProps: {
                                        sx: isSmallerThanSmall ? { height: '30%' } : {}
                                    }
                                }}>
                                {!reviewTimesLoading &&
                                    reviewTimes &&
                                    reviewTimes.map((data) => (
                                        <MenuItem key={data.id} value={data.minutes}>
                                            {data.isDefault
                                                ? data.minutes + ' Minutes'
                                                : data.minutes + ' Minutes'}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </Box>
                        {isReasonShow && (
                            <>
                                <Box>
                                    <DialogContentText>
                                        Reasoning for extended review
                                    </DialogContentText>
                                    <Select
                                        fullWidth
                                        displayEmpty
                                        size="small"
                                        value={reasonForExtendedReview}
                                        onChange={reasonForExtendedReviewChangeHandler}>
                                        {!reviewExtendReasonsLoading &&
                                            reviewExtendReasons &&
                                            reviewExtendReasons.map((data) => (
                                                <MenuItem key={data.id} value={data.reason}>
                                                    {data.reason}
                                                </MenuItem>
                                            ))}
                                        <MenuItem value="other">Other</MenuItem>
                                    </Select>
                                </Box>
                                {reasonForExtendedReview === 'other' && (
                                    <TextField
                                        multiline
                                        fullWidth
                                        placeholder="Enter reason"
                                        value={otherFreeText}
                                        onChange={otherFreeTextChangeHandler}
                                    />
                                )}
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        type="submit"
                        loading={!resolved}
                        loadingPosition="start"
                        variant="contained"
                        startIcon={<Check />}>
                        Submit
                    </LoadingButton>
                    <Button startIcon={<Cancel />} variant="outlined" onClick={clickCancelHandler}>
                        Cancel
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default SubmitReviewModal;
